import React, { useState, useEffect, useCallback } from 'react';
import { Subscription, UsageStats, SubscriptionResponse, TierInfo, SubscriptionTier } from '../../types/subscription';
import { useLocation } from 'react-router-dom';
import PayPalButton from '../PayPalButton/PayPalButton';
import './SubscriptionPage.css';

const SubscriptionPage: React.FC = () => {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [usage, setUsage] = useState<UsageStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const location = useLocation();

  const loadSubscription = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { apiService } = await import('../../services/api');
      const data = await apiService.getSubscription();
      setSubscription(data.subscription as Subscription);
      setUsage(data.usage);
    } catch (err) {
      console.error('Failed to load subscription:', err);
      setError(err instanceof Error ? err.message : 'Failed to load subscription');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSubscription();
  }, [location, loadSubscription]);

  const handleCancelSubscription = async () => {
    try {
      setError(null);
      
      const { apiService } = await import('../../services/api');
      const data = await apiService.cancelSubscription();
      setShowCancelConfirm(false);
      
      // Reload subscription to show updated status
      await loadSubscription();
      
      alert(data.message || 'Subscription cancelled successfully');
    } catch (err) {
      console.error('Failed to cancel subscription:', err);
      setError(err instanceof Error ? err.message : 'Failed to cancel subscription');
    }
  };

  const formatStorageSize = (bytes: number): string => {
    const gb = bytes / (1024 * 1024 * 1024);
    return gb.toFixed(2);
  };

  const calculateProgress = (used: number, limit: number): number => {
    if (limit === 0) return 0;
    return Math.min((used / limit) * 100, 100);
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const renderProgressBar = (used: number, limit: number, label: string, unit: string = '') => {
    const progress = calculateProgress(used, limit);
    const displayUsed = unit === 'GB' ? formatStorageSize(used) : used;
    const displayLimit = unit === 'GB' ? formatStorageSize(limit) : limit;
    
    return (
      <div className="usage-item">
        <div className="usage-label">{label}</div>
        <div className="progress-bar-container">
          <div 
            className="progress-bar-fill" 
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="usage-stats">
          {displayUsed}{unit} / {displayLimit}{unit}
        </div>
      </div>
    );
  };

  const renderTierCard = (tier: TierInfo) => {
    const isCurrentTier = subscription?.tier === tier.name;
    
    return (
      <div className={`tier-card ${isCurrentTier ? 'current-tier' : ''}`} key={tier.name}>
        <div className="tier-header">
          <h3 className="tier-name">{tier.name}</h3>
          <div className="tier-price">
            ${tier.priceMonthly}
            <span className="price-period">/month</span>
          </div>
        </div>
        <div className="tier-features">
          {tier.features.map((feature, index) => (
            <div key={index} className="tier-feature">
              ✓ {feature}
            </div>
          ))}
        </div>
        {!isCurrentTier && tier.paypalPlanId && (
          <div className="tier-action">
            {renderPayPalButton(tier.paypalPlanId, tier.name)}
          </div>
        )}
        {isCurrentTier && (
          <div className="current-tier-badge">Current Plan</div>
        )}
      </div>
    );
  };

  const renderPayPalButton = (planId: string, tierName: SubscriptionTier) => {
    return (
      <PayPalButton
        planId={planId}
        tierName={tierName}
        onSuccess={() => {
          console.log(`Successfully subscribed to ${tierName}`);
        }}
        onError={(error) => {
          console.error(`Failed to subscribe to ${tierName}:`, error);
          setError(error.message);
        }}
      />
    );
  };

  const availableTiers: TierInfo[] = [
    {
      name: 'Free',
      priceMonthly: 0,
      cratesLimit: 2,
      storageLimit: 5 * 1024 * 1024 * 1024, // 5GB
      sharesLimit: 2,
      features: [
        '2 Data Crates',
        '5GB Storage',
        '2 Shares/month',
        '1 Secret/month',
        'Basic Support'
      ]
    },
    {
      name: 'Pro',
      priceMonthly: 10,
      cratesLimit: 50,
      storageLimit: 50 * 1024 * 1024 * 1024,
      sharesLimit: 100,
      paypalPlanId: 'P-06036170F2246724FNGYJVZQ',
      features: [
        '50 Data Crates',
        '50GB Storage',
        '100 Shares/month',
        '10 Secrets/month',
        'Priority Support',
        'Advanced Analytics'
      ]
    },
    {
      name: 'Enterprise',
      priceMonthly: 50,
      cratesLimit: 500,
      storageLimit: 500 * 1024 * 1024 * 1024,
      sharesLimit: 1000,
      paypalPlanId: 'P-8VW59376Y7725280RNGYJVZQ',
      features: [
        '500 Data Crates',
        '500GB Storage',
        '1000 Shares/month',
        '50 Secrets/month',
        '24/7 Premium Support',
        'Advanced Analytics',
        'Custom Integrations',
        'Dedicated Account Manager'
      ]
    }
  ];

  if (loading) {
    return (
      <div className="subscription-page">
        <div className="loading-message">Loading subscription details...</div>
      </div>
    );
  }

  if (error && !subscription) {
    return (
      <div className="subscription-page">
        <div className="error-message">{error}</div>
        <button onClick={loadSubscription} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="subscription-page">
      <h1 className="page-title">Subscription Management</h1>

      {/* Current Subscription Section */}
      {subscription && usage && (
        <div className="current-subscription-section">
          <div className="subscription-header">
            <div className="subscription-info">
              <div className="info-row">
                <span className="info-label">Current Plan:</span>
                <span className="info-value tier-badge">{subscription.tier}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Status:</span>
                <span className={`info-value status-badge status-${subscription.status}`}>
                  {subscription.status.replace('_', ' ').toUpperCase()}
                </span>
              </div>
              {subscription.nextBillingDate && subscription.status === 'active' && (
                <div className="info-row">
                  <span className="info-label">Next Billing:</span>
                  <span className="info-value">{formatDate(subscription.nextBillingDate)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Usage Statistics */}
          <div className="usage-section">
            <h2 className="section-title">Usage Statistics</h2>
            <div className="usage-grid">
              {renderProgressBar(usage.cratesUsed, usage.cratesLimit, 'Crates')}
              {renderProgressBar(usage.storageUsed, usage.storageLimit, 'Storage', 'GB')}
              <div>
                {renderProgressBar(usage.sharesUsed, usage.sharesLimit, 'Shares (this month)')}
                {usage.sharesUsed >= usage.sharesLimit && (
                  <div className="quota-warning">Monthly share quota reached — resets next month.</div>
                )}
              </div>
              <div>
                {renderProgressBar(usage.secretsUsed, usage.secretsLimit, 'Secrets (this month)')}
                {usage.secretsUsed >= usage.secretsLimit && (
                  <div className="quota-warning">Monthly secret quota reached — resets next month.</div>
                )}
              </div>
            </div>
          </div>

          {/* Cancellation / expiry notice */}
          {(subscription.status === 'cancelled' || subscription.status === 'expired') && (
            <div className="subscription-notice subscription-notice-cancelled">
              Your subscription has been cancelled. You are now on the <strong>Free plan</strong>.
              You can re-subscribe below at any time.
            </div>
          )}

          {subscription.status === 'payment_failed' && (
            <div className="subscription-notice subscription-notice-warning">
              Your last payment failed. Please update your payment method with PayPal to restore access.
            </div>
          )}

          {/* Cancel Subscription Button */}
          {subscription.status === 'active' && subscription.tier !== 'Free' && (
            <div className="cancel-section">
              {!showCancelConfirm ? (
                <button 
                  onClick={() => setShowCancelConfirm(true)}
                  className="cancel-button"
                >
                  Cancel Subscription
                </button>
              ) : (
                <div className="cancel-confirm">
                  <p className="confirm-message">
                    Are you sure you want to cancel your subscription? 
                    You will lose access to premium features at the end of your billing period.
                  </p>
                  <div className="confirm-actions">
                    <button 
                      onClick={handleCancelSubscription}
                      className="confirm-cancel-button"
                    >
                      Yes, Cancel
                    </button>
                    <button 
                      onClick={() => setShowCancelConfirm(false)}
                      className="keep-subscription-button"
                    >
                      Keep Subscription
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Available Plans Section */}
      <div className="available-plans-section">
        <h2 className="section-title">Available Plans</h2>
        <div className="tiers-grid">
          {availableTiers.map(tier => renderTierCard(tier))}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="error-banner">
          {error}
        </div>
      )}
    </div>
  );
};

export default SubscriptionPage;

